import type { QueryView } from '@krp/client-runtime'

import type { CommandFeedback } from '../../../shared/model/feedback'
import type {
  FiscalState,
  PaymentKind,
  PaymentState,
  AqsiSaleAttention,
  SaleOperationVm,
  StateBadgeVm,
} from '../../sale/model/types'

/**
 * Позиция возврата — снимок сервера: имя, цена и сумма из ИСХОДНОГО чека
 * (C4b), а не из сегодняшней карточки товара.
 */
export interface AqsiRefundLine {
  id: string
  /** Номер позиции в исходном чеке. */
  ordinal: number
  name: string
  /** Количество строкой — как пришло, без арифметики с плавающей точкой. */
  quantity: string
  priceKopecks: number
  amountKopecks: number
}

/**
 * Возврат (`aqsi.sale` вида `Refund`, C4b) — живёт на сервере. Деньги и чек —
 * раздельно, как у продажи: та же машина состояний и тот же журнал операций.
 */
export interface AqsiRefund {
  id: string
  /** Номер исходного чека кабинета ЭТОГО rid. */
  sourceReceiptId: string
  createdAt: string
  /** Способ возврата — способ исходной оплаты: наличные наличными, карта — на ту же карту. */
  paymentKind: PaymentKind
  totalKopecks: number
  payment: PaymentState
  fiscal: FiscalState
  /** Наш UUID операции денег возврата (`aqsi.refund.payment`). */
  purchaseOperationId: string | null
  /** Слип самого возврата у кабинета (у наличных — нет). */
  slipId: string | null
  receiptOperationId: string | null
  receiptId: string | null
  lastMessage: string | null
  isFinished: boolean
  lines: readonly AqsiRefundLine[]
  /** Предупреждения службы по чеку возврата (RP-FB35 N4): почему чек не ушёл сам и когда служба повторит. */
  attention: readonly AqsiSaleAttention[]
  version: number
}

/**
 * Что сейчас на кассе места (`GET sales/active`): служба держит одну
 * незавершённую продажу ИЛИ возврат на кассу. Продажу окно возврата не
 * разбирает — только называет.
 */
export type SeatActive = { kind: 'none' } | { kind: 'sale'; saleId: string } | { kind: 'refund'; refund: AqsiRefund }

// ——— Намерение (slice) ———

/**
 * Что человек отметил к возврату: количество по номеру позиции исходного чека,
 * как ввёл (строкой). Цену, ставку и способ возврата браузер не присылает.
 */
export interface RefundDraft {
  quantities: Record<string, string>
}

export interface RefundsIntentState {
  /** Ключ — `<rid>|<receiptId>`: одинаковые номера чеков двух AQSI — разные черновики. */
  drafts: Record<string, RefundDraft>
  /** Завершённый в этом окне возврат — показать итог: ключ `<rid>|<receiptId>` → id возврата. */
  finished: Record<string, string>
}

/** Опасная команда возврата, исход которой клиент ещё ждёт. */
export type RefundCommandKind = 'create' | 'pay' | 'fiscalize' | 'cancel'

/**
 * - `sending` — POST ушёл, ответа нет (после F5 читается как `unknown`);
 * - `unknown` — исход неизвестен: повтор запрещён, состояние читается;
 * - `not-arrived` — служба за окно отправки так и не узнала команду: можно новую (новый ключ).
 */
export type RefundCommandPhase = 'sending' | 'unknown' | 'not-arrived'

export interface RefundCommand {
  resourceId: string
  registerKey: string
  /** Касса кабинета, на которой идёт работа (RP-C9): адрес команды после F5. */
  deviceId: number
  kind: RefundCommandKind
  /** Исходный чек — по нему чтение кассы узнаёт созданный возврат. */
  receiptId: string
  /** Возврат, над которым команда; у `create` — `null`. */
  refundId: string | null
  /** Версия возврата (у `create` — версия сессии), с которой ушла команда. */
  version: number
  /** Наш UUID операции — им служба назовёт операцию денег/чека возврата. */
  operationId: string
  startedAt: string
  phase: RefundCommandPhase
}

export interface RefundCommandsState {
  /** Ключ — `<rid>|<registerKey>`: одна опасная команда на кассу места. */
  pending: Record<string, RefundCommand>
}

// ——— vm ———

export interface RefundSourceLineVm {
  ordinal: number
  name: string
  soldText: string
  returnedText: string
  availableText: string
  /** Остаток для «Вернуть всё» — числом в строке «1.5», как вводит человек. */
  availableValue: string
  priceText: string
  /** Что ввёл человек; пусто — позицию не возвращают. */
  quantity: string
  /** Почему количество не годится — словами. */
  error: string | null
  /** Вернуть по позиции уже нечего. */
  exhausted: boolean
}

export interface RefundProgressVm {
  id: string
  totalText: string
  paymentText: string
  lines: readonly { key: string; name: string; quantityText: string; amountText: string }[]
  money: StateBadgeVm
  receipt: StateBadgeVm
  lastMessage: string | null
  slipText: string | null
  /** «Важно: …» / «Внимание: …» от службы - слово уровня, текст службы дословно, «Служба повторит в ЧЧ:ММ». */
  attentionLines: readonly string[]
  version: number
}

export interface RefundCommandVm {
  kindText: string
  phase: RefundCommandPhase
  text: string
}

/**
 * Этап окна:
 * - `loading` — чек, касса или её возврат ещё читаются;
 * - `blocked` — вернуть отсюда нельзя (причина словами);
 * - `select` — отметить, что вернуть;
 * - `refund` — возврат этого чека идёт на кассе;
 * - `finished` — возврат завершён в этом окне.
 */
export type RefundStage = 'loading' | 'blocked' | 'select' | 'refund' | 'finished'

export interface RefundVm {
  resourceId: string
  receiptId: string
  /** Чек и касса: что не загрузилось — своим состоянием. */
  receiptQuery: QueryView
  seatQuery: QueryView | null
  stage: RefundStage
  title: string
  header: { atText: string; totalText: string; paymentText: string; returnedText: string } | null
  blockedReason: string | null
  /** Какое окно предложить, если вернуть нельзя: РМК, продажа или возврат другого чека. */
  blockedAction: { kind: 'rmk' } | { kind: 'sale' } | { kind: 'refund'; receiptId: string } | null
  lines: readonly RefundSourceLineVm[]
  canCreate: boolean
  createBlockedReason: string | null
  refund: RefundProgressVm | null
  canPay: boolean
  payText: string
  canFiscalize: boolean
  canCancel: boolean
  operation: SaleOperationVm | null
  command: RefundCommandVm | null
  /** Итог: чек возврата пробит или возврат отменён (деньги не двигались). */
  finished: { receiptId: string | null; totalText: string; paymentText: string; cancelled: boolean } | null
  /** Отмена оплаты до сверки итогов (reversal) службой не поддерживается — словами, а не скрытой кнопкой. */
  reversalNote: string
  feedback: CommandFeedback | null
}

export interface RefundActions {
  quantityChanged: (ordinal: number, quantity: string) => void
  returnAll: () => void
  clear: () => void
  /** После итога — оформить ещё один возврат по тому же чеку (остаток). */
  startNew: () => void
  create: () => void
  pay: () => void
  fiscalize: () => void
  cancel: () => void
  checkStatus: () => void
  reconcile: () => void
  openManualResolve: () => void
  openBlocked: () => void
  reload: () => void
  close: () => void
  dismissFeedback: () => void
}

/** Право оформлять возврат по чеку (политика `refunds.manage` на ресурсе). */
export const REFUNDS_CAPABILITY = 'refunds.manage'

/** Вход «Возврат по номеру чека»: человек вводит или сканирует номер, окно возврата открывается по нему. */
export interface RefundLookupActions {
  open: (receiptId: string) => void
}
